import * as React from 'react';

import type {GetAccountBilling_accountBilling_currentPlan as AccountPlan} from 'nutshell-graphql-types';

import {ErrorState} from '../../error-state/error-state';
import {LoadingPage} from '../../loading-page/loading-page';

import {DescriptionCell} from './description-cell';
import {getAddOnsIncludedInPlan, getPlanDetailText, getAnnualSavings} from './helpers';
import {PriceCell} from './price-cell';
import {TableSectionHeader} from './table-section-header';

import styles from './billing-summary.module.css';

type Props = {
    plan: AccountPlan | null | undefined;
    isLoading: boolean;
    includeHeaderLinks?: boolean;
    isPurchasing?: boolean;
    errorMessage: string | null | undefined;
};

export function BillingSummary(props: Props) {
    const crmPlan = props.plan && props.plan.crm;
    const engagementPlan = props.plan && props.plan.inbox;
    const marketingSuitePlan = props.plan && props.plan.marketingSuite;
    const marketing = props.plan && props.plan.marketing;
    const includedAddOns = getAddOnsIncludedInPlan(props.plan);

    const isBilledAnnually = crmPlan && crmPlan.cycleText === 'ANNUALLY';
    const shouldShowSubtotal =
        props.plan && props.plan.totalMonthDisplay !== props.plan.subtotalMonthDisplay;

    const headerRow = (
        <thead className={styles.header}>
            <tr>
                <td>Description</td>
                <td>Qty</td>
                <td>Price</td>
                <td>Amount</td>
            </tr>
        </thead>
    );

    const loadingState = (
        <tbody className={styles.body}>
            <tr>
                {/* @ts-expect-error */}
                <td colSpan='4'>
                    <LoadingPage />
                </td>
            </tr>
        </tbody>
    );

    const errorState = (
        <tbody className={styles.body}>
            <tr>
                {/* @ts-expect-error */}
                <td colSpan='4'>
                    <ErrorState errorMessage={props.errorMessage} size='small' />
                </td>
            </tr>
        </tbody>
    );

    return (
        <table className={styles.table}>
            <TableSectionHeader
                linkProps={
                    props.includeHeaderLinks
                        ? {
                              to: '/setup/billing/sales',
                              text: 'Manage plan',
                          }
                        : undefined
                }
                title={props.isPurchasing ? 'Selected plan' : 'Your plan'}
            />
            {headerRow}
            {props.isLoading && loadingState}
            {props.errorMessage && errorState}
            {!props.isLoading && !props.errorMessage && props.plan && (
                <>
                    {crmPlan && (
                        <tbody className={styles.body}>
                            <tr>
                                <DescriptionCell
                                    description={'Nutshell Sales'}
                                    tier={crmPlan.currentTier}
                                    isBold={true}
                                    detail={getPlanDetailText(crmPlan)}
                                    coupons={crmPlan.coupon ? [crmPlan.coupon] : []}
                                    customRow={isBilledAnnually && getAnnualSavings(crmPlan)}
                                />
                                <td>{crmPlan.quantity}</td>
                                <PriceCell
                                    stickerPrice={crmPlan.stickerUnitAmountDisplay}
                                    finalPrice={crmPlan.unitAmountDisplay}
                                />
                                <PriceCell finalPrice={crmPlan.totalDisplay} />
                            </tr>
                        </tbody>
                    )}
                    {marketingSuitePlan && (
                        <tbody className={styles.body}>
                            <tr>
                                <DescriptionCell
                                    description={'Nutshell Marketing'}
                                    tier={marketingSuitePlan.currentTier}
                                    isBold={true}
                                    detail={`Entire company`}
                                    coupons={[
                                        ...(marketingSuitePlan.coupon
                                            ? [marketingSuitePlan.coupon]
                                            : []),
                                    ]}
                                />
                                <td></td>
                                <PriceCell
                                    stickerPrice={marketingSuitePlan.stickerUnitAmountDisplay}
                                    finalPrice={marketingSuitePlan.unitAmountDisplay}
                                />
                                <PriceCell finalPrice={marketingSuitePlan.totalDisplay} />
                            </tr>
                            {marketing && (
                                <>
                                    <tr className={styles['email-marketing-row']}>
                                        <div className={styles['email-marketing-description']}>
                                            <DescriptionCell
                                                description='Email marketing'
                                                detail={getPlanDetailText(marketing)}
                                                coupons={marketing.coupon ? [marketing.coupon] : []}
                                                leftBorder={true}
                                            />
                                        </div>
                                        <td></td>
                                        <PriceCell
                                            stickerPrice={marketing.stickerUnitAmountDisplay}
                                            finalPrice={marketing.unitAmountDisplay}
                                        />
                                        <PriceCell finalPrice={marketing.totalDisplay} />
                                    </tr>
                                </>
                            )}
                        </tbody>
                    )}
                    {engagementPlan && (
                        <tbody className={styles.body}>
                            <tr>
                                <DescriptionCell
                                    description={'Nutshell Engagement'}
                                    tier={engagementPlan.currentTier}
                                    isBold={true}
                                    detail={getPlanDetailText(engagementPlan)}
                                    coupons={engagementPlan.coupon ? [engagementPlan.coupon] : []}
                                />
                                <td>{engagementPlan.quantity}</td>
                                <PriceCell
                                    stickerPrice={engagementPlan.stickerUnitAmountDisplay}
                                    finalPrice={engagementPlan.unitAmountDisplay}
                                />
                                <PriceCell finalPrice={engagementPlan.totalDisplay} />
                            </tr>
                        </tbody>
                    )}
                    {includedAddOns.length > 0 && (
                        <TableSectionHeader
                            linkProps={
                                props.includeHeaderLinks
                                    ? {
                                          to: '/setup/billing/add-ons',
                                          text: 'Manage add-ons',
                                      }
                                    : undefined
                            }
                            title={props.isPurchasing ? 'Selected add-ons' : 'Your add-ons'}
                        />
                    )}
                    {includedAddOns.map((addOn) => (
                        <tbody key={addOn.planCode} className={styles.body}>
                            <tr>
                                <DescriptionCell
                                    description={addOn.nameDisplay}
                                    detail={getPlanDetailText(addOn)}
                                    coupons={addOn.coupon ? [addOn.coupon] : []}
                                    customRow={
                                        !addOn.isFake &&
                                        addOn.cycleText === 'ANNUALLY' &&
                                        getAnnualSavings(addOn)
                                    }
                                />
                                <td></td>
                                <PriceCell
                                    stickerPrice={addOn.stickerUnitAmountDisplay}
                                    finalPrice={addOn.unitAmountDisplay}
                                />
                                <PriceCell finalPrice={addOn.totalDisplay} />
                            </tr>
                        </tbody>
                    ))}
                    {/* This looks backwards, but footers display in reverse order! */}
                    <tfoot className={styles.footer}>
                        <tr>
                            <DescriptionCell description='Monthly total' />
                            {/* Next 2 cells purposely empty */}
                            <td></td>
                            <td></td>
                            <PriceCell finalPrice={props.plan.totalMonthDisplay} />
                        </tr>
                    </tfoot>
                    {shouldShowSubtotal && (
                        <tfoot className={styles.footer}>
                            <tr>
                                <DescriptionCell
                                    description='Subtotal'
                                    coupons={props.plan ? props.plan.accountCoupons : []}
                                />
                                {/* Next 2 cells purposely empty */}
                                <td></td>
                                <td></td>
                                <PriceCell
                                    finalPrice={props.plan.subtotalMonthDisplay}
                                    discount={props.plan.accountDiscountDisplay}
                                />
                            </tr>
                        </tfoot>
                    )}
                </>
            )}
        </table>
    );
}
